/**
 * Formulario de contacto guiado: motivos y preguntas.
 *
 * Sale del análisis de 2.695 mensajes de contacto de las webs de las notarías (junio 2024 - octubre 2026):
 * 8 de cada 10 son para un trámite nuevo y, de esos, uno de cada tres no dice si quiere cita, precio o documentación.
 * Cada motivo pregunta solo lo que la notaría necesita para responder a la primera.
 *
 * Los textos son claves en español que se traducen con t() (data/solicitudWebTextos). Lo que se envía a la notaría
 * es siempre la clave en español (pregunta y opción elegida), así el email le llega en español sea cual sea el idioma.
 */

// fecha: día pasado (hasta hoy); fechaFutura: día desde hoy, sin hora (ref: https://trello.com/c/pYpZdaPv);
// fechaHora: día desde hoy y hora (refs: https://trello.com/c/FlzpRILR, https://trello.com/c/mm9XRZhS)
export type TipoCampo = 'opciones' | 'texto' | 'fecha' | 'fechaFutura' | 'fechaHora'

export interface CampoContacto {
  /** Pregunta (clave en español). */
  clave: string
  tipo: TipoCampo
  opciones?: string[]
  /** Texto de ejemplo dentro del campo (clave en español). */
  ejemplo?: string
  /** Aclaración bajo la pregunta (clave en español). */
  ayuda?: string
  /** Aviso que aparece al elegir una opción concreta: { opción: aviso } (claves en español). */
  avisos?: Record<string, string>
  obligatorio?: boolean
}

export interface TramiteContacto {
  codigo: string
  /** Nombre (clave en español). */
  nombre: string
  icono: string
  /** Está en el catálogo del encargo online (se puede ofrecer hacerlo online). */
  online: boolean
  campos: CampoContacto[]
}

export type CodigoMotivo = 'TRAMITE' | 'COPIA' | 'FACTURA' | 'ESTADO' | 'CITA' | 'OTRA'

export interface MotivoContacto {
  codigo: CodigoMotivo
  titulo: string
  descripcion: string
  icono: string
  /** Copias, facturas y estado: con la Notaría Virtual el cliente ya lo puede ver allí. */
  ofrecerNV?: boolean
  campos: CampoContacto[]
}

const SI_NO = ['Sí', 'No']
const SI_NO_NS = ['Sí', 'No', 'No lo sé']

export const MOTIVOS_CONTACTO: MotivoContacto[] = [
  {
    codigo: 'TRAMITE', titulo: 'Quiero hacer un trámite', descripcion: 'Compraventa, herencia, testamento, poder…',
    icono: 'mdi-file-document-edit-outline', campos: []
  },
  {
    codigo: 'COPIA', titulo: 'Necesito una copia de una escritura', descripcion: 'De una escritura que ya se firmó',
    icono: 'mdi-content-copy', ofrecerNV: true,
    campos: [
      { clave: '¿De qué escritura?', tipo: 'texto', ejemplo: 'Por ejemplo: compraventa del piso de la calle Mayor', obligatorio: true },
      // Con un firmante el copista localiza la escritura; el DNI ayuda pero es opcional (minimización de datos)
      { clave: 'Nombre y apellidos de uno de los firmantes', tipo: 'texto', ejemplo: 'Por ejemplo: María López García', obligatorio: true },
      { clave: 'DNI o NIE de ese firmante (si lo tiene)', tipo: 'texto', ayuda: 'Ayuda a localizar la escritura.' },
      // Dice a la notaría si puede entregar la copia autorizada
      {
        clave: '¿Cuál es su relación con la escritura?', tipo: 'opciones', obligatorio: true,
        opciones: ['Firmé yo', 'Soy heredero/a', 'Tengo un poder', 'Otro motivo']
      },
      { clave: 'Fecha aproximada de la firma', tipo: 'fecha' },
      { clave: 'Número de protocolo (si lo tiene)', tipo: 'texto' },
      { clave: 'Notario que la autorizó (si no fue esta notaría)', tipo: 'texto' },
      {
        clave: '¿Qué copia necesita?', tipo: 'opciones', opciones: ['Simple', 'Autorizada', 'No lo sé'],
        ayuda: 'La copia simple sirve para consultar. La autorizada tiene validez legal.',
        avisos: { Autorizada: 'La copia autorizada solo se entrega a quienes intervienen en la escritura o acreditan un interés legítimo, como herederos o apoderados.' }
      },
      { clave: '¿Para qué la necesita?', tipo: 'texto', ejemplo: 'Por ejemplo: para el banco, para el Registro, para vender' }
    ]
  },
  {
    codigo: 'FACTURA', titulo: 'Necesito una factura', descripcion: 'De una escritura que ya se firmó',
    icono: 'mdi-receipt-text-outline', ofrecerNV: true,
    campos: [
      { clave: '¿De qué escritura?', tipo: 'texto', ejemplo: 'Por ejemplo: compraventa del piso de la calle Mayor', obligatorio: true },
      { clave: 'Fecha aproximada de la firma', tipo: 'fecha' },
      { clave: '¿A nombre de quién?', tipo: 'texto', ejemplo: 'Persona o empresa, con su DNI o NIF' }
    ]
  },
  {
    codigo: 'ESTADO', titulo: '¿Cómo va mi asunto?', descripcion: 'Inscripción, impuestos o recoger la escritura',
    icono: 'mdi-progress-clock', ofrecerNV: true,
    campos: [
      { clave: '¿De qué escritura?', tipo: 'texto', ejemplo: 'Por ejemplo: compraventa del piso de la calle Mayor', obligatorio: true },
      { clave: 'Fecha de la firma o referencia', tipo: 'texto' },
      {
        clave: '¿Qué quiere saber?', tipo: 'opciones',
        opciones: ['Si está inscrita en el Registro', 'Los impuestos', 'Cuándo puedo recogerla', 'Otra cosa']
      }
    ]
  },
  {
    codigo: 'CITA', titulo: 'Tengo una cita', descripcion: 'Confirmarla, cambiarla o anularla',
    icono: 'mdi-calendar-clock',
    campos: [
      { clave: 'Día y hora de la cita', tipo: 'fechaHora', obligatorio: true },
      { clave: '¿Qué quiere hacer?', tipo: 'opciones', opciones: ['Confirmarla', 'Cambiarla', 'Anularla'], obligatorio: true }
    ]
  },
  {
    codigo: 'OTRA', titulo: 'Otra consulta', descripcion: 'Cualquier otra pregunta',
    icono: 'mdi-help-circle-outline', campos: []
  }
]

/** Trámites más pedidos en los mensajes de contacto. Los del catálogo del encargo online se pueden hacer online. */
export const TRAMITES_CONTACTO: TramiteContacto[] = [
  {
    codigo: 'COMPRAVENTA', nombre: 'Compraventa', icono: 'mdi-home-switch-outline', online: true,
    campos: [
      { clave: '¿Compra o vende?', tipo: 'opciones', opciones: ['Compro', 'Vendo'] },
      { clave: 'Precio aproximado', tipo: 'texto', ayuda: 'Con el precio la notaría puede darle un presupuesto aproximado.' },
      { clave: '¿Con hipoteca?', tipo: 'opciones', opciones: SI_NO_NS },
      { clave: 'Fecha prevista de firma', tipo: 'fechaFutura' }
    ]
  },
  {
    codigo: 'HIPOTECA', nombre: 'Préstamo hipotecario', icono: 'mdi-bank-outline', online: true,
    campos: [
      { clave: 'Banco', tipo: 'texto' },
      { clave: 'Importe aproximado', tipo: 'texto' },
      { clave: '¿Tiene ya la FEIN del banco?', tipo: 'opciones', opciones: SI_NO_NS }
    ]
  },
  {
    codigo: 'HERENCIA', nombre: 'Herencia', icono: 'mdi-account-group-outline', online: true,
    campos: [
      { clave: '¿Qué necesita?', tipo: 'opciones', opciones: ['Aceptar la herencia', 'Renunciar a la herencia', 'Declaración de herederos', 'No lo sé'] },
      { clave: '¿Había testamento?', tipo: 'opciones', opciones: SI_NO_NS },
      { clave: 'Número de herederos', tipo: 'texto' }
    ]
  },
  {
    codigo: 'TESTAMENTO', nombre: 'Testamento', icono: 'mdi-file-sign', online: true,
    campos: [
      { clave: '¿Para quién?', tipo: 'opciones', opciones: ['Solo para mí', 'Para mí y mi pareja'] },
      { clave: '¿Tiene un testamento anterior?', tipo: 'opciones', opciones: SI_NO }
    ]
  },
  {
    codigo: 'MATRIMONIO', nombre: 'Matrimonio y capitulaciones', icono: 'mdi-ring', online: true,
    campos: [
      { clave: '¿Qué necesita?', tipo: 'opciones', opciones: ['Casarnos ante notario', 'Pareja de hecho', 'Separación de bienes (capitulaciones)', 'No lo sé'] },
      { clave: 'Nacionalidad de cada uno', tipo: 'texto' }
    ]
  },
  {
    codigo: 'PODER', nombre: 'Poder', icono: 'mdi-account-key-outline', online: true,
    campos: [
      { clave: '¿Para qué?', tipo: 'opciones', opciones: ['Comprar o vender', 'Herencia', 'Banco', 'Juicios (abogado y procurador)', 'General'] },
      { clave: '¿Se usará en otro país?', tipo: 'opciones', opciones: SI_NO }
    ]
  },
  {
    codigo: 'DONACION', nombre: 'Donación', icono: 'mdi-gift-outline', online: true,
    campos: [
      { clave: '¿Qué se dona?', tipo: 'opciones', opciones: ['Dinero', 'Un inmueble', 'Otra cosa'] },
      { clave: 'Importe aproximado', tipo: 'texto' },
      { clave: 'Parentesco', tipo: 'texto', ejemplo: 'Por ejemplo: de padres a hija' }
    ]
  },
  {
    codigo: 'FIRMA', nombre: 'Legitimar una firma o copia', icono: 'mdi-draw-pen', online: false,
    campos: [
      { clave: 'Tipo de documento', tipo: 'texto' },
      { clave: '¿Se usará en otro país?', tipo: 'opciones', opciones: SI_NO }
    ]
  },
  {
    codigo: 'SOCIEDAD', nombre: 'Sociedades', icono: 'mdi-domain', online: true, campos: []
  },
  // Mismos trámites que el encargo online (ref: https://trello.com/c/RjJ2IbDY)
  {
    codigo: 'EXTRANJERIA', nombre: 'Extranjería y nacionalidad', icono: 'mdi-passport', online: true, campos: []
  },
  {
    codigo: 'ACTAS', nombre: 'Actas y otros documentos notariales', icono: 'mdi-file-certificate-outline', online: true, campos: []
  },
  {
    codigo: 'OTRO', nombre: 'Otro trámite', icono: 'mdi-dots-horizontal-circle-outline', online: true, campos: []
  }
]

/**
 * Qué quiere del trámite: cita o precio, lo mismo que la cita y el presupuesto del encargo online. La documentación ya no
 * se pregunta: se le enseña al elegir el trámite (la del catálogo del encargo). Ref: https://trello.com/c/63NGWLHt
 */
export const NECESIDADES_TRAMITE = [
  { codigo: 'CITA', texto: 'Pedir cita' },
  { codigo: 'PRECIO', texto: 'Saber el precio' }
]
